import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import MoneyInput from '../../common/MoneyInput';
import LegacyIndividualAffordabilityFields, { legacyIndividualAffordabilityGroups } from './LegacyIndividualAffordabilityFields';
import OtherLivingCostFields from './OtherLivingCostFields';
import { capturedAffordability, hasIndividualBreakdown, individualBreakdownGroups, individualBreakdownTotals,
  getPreviousIndividualFinancials, PreviousIndividualFinancials, otherLivingExpenseTotal, sumMonthlyAmounts } from '../../../services/individualAffordability';
import { useWizardCustomerId } from '../wizardCustomer';
import './IndividualAffordabilityFields.css';

const money = (value?: number) => value == null ? 'Not captured' : `R ${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export default function IndividualAffordabilityFields({ includeIncome = true, showHeading = true }: { includeIncome?: boolean; showHeading?: boolean } = {}) {
  const { control, setValue, getValues, getFieldState, formState: { errors } } = useFormContext();
  const customerPartyId = useWizardCustomerId();
  const financial = useWatch({ control, name: 'financial' });
  const net = useWatch({ control, name: 'individual.monthlyNetIncome' });
  const [editRecorded, setEditRecorded] = useState(false);
  const [captureBreakdown, setCaptureBreakdown] = useState(false);
  const recordedHelpId = useId();
  const detailed = hasIndividualBreakdown(financial), totals = individualBreakdownTotals(financial);
  const figures = capturedAffordability({ financial, individual: { monthlyNetIncome: net } });
  const readRecorded = useCallback(() => {
    const f = getValues('financial');
    const hasLegacyExpenses = legacyIndividualAffordabilityGroups[1].fields.slice(0, 7).some(item =>
      item.name.split('.').slice(1).reduce<any>((value, key) => value?.[key], f) > 0);
    // Profile take-home pay and shared debt fields don't identify the old capture format.
    const hasRecorded = f?.income?.grossIncome > 0 || f?.income?.otherIncome > 0 || hasLegacyExpenses;
    return hasIndividualBreakdown(f) || !hasRecorded ? undefined : { ...f, income: { ...f?.income },
      expenses: { ...f?.expenses }, household: { ...f?.household },
      netIncome: getValues('individual.monthlyNetIncome') ?? f?.income?.netIncome };
  }, [getValues]);
  const [recorded, setRecorded] = useState(readRecorded);
  const [previous, setPrevious] = useState<{ customer: string; data?: PreviousIndividualFinancials | null; failed?: boolean }>();
  const [retryPrevious, setRetryPrevious] = useState(0);
  const [appliedFrom, setAppliedFrom] = useState<string>();
  const attemptedPrevious = useRef<string | undefined>(undefined);
  const applyPrevious = useCallback((saved: PreviousIndividualFinancials) => {
    const f = saved.financialDetails;
    setValue('financial', { ...f, affordabilityCaptured: true }, { shouldDirty: true });
    setValue('individual.monthlyNetIncome', f.income?.netIncome, { shouldDirty: true });
    setValue('individual.monthlyLivingExpenses', f.livingExpenses, { shouldDirty: true });
    setRecorded(readRecorded());
    setEditRecorded(!hasIndividualBreakdown(f));
    setAppliedFrom(saved.applicationId);
  }, [readRecorded, setValue]);
  useEffect(() => {
    setPrevious(undefined);
    setAppliedFrom(undefined);
    if (!customerPartyId || !includeIncome) return;
    const controller = new AbortController();
    getPreviousIndividualFinancials(customerPartyId, controller.signal).then(data => {
      if (!controller.signal.aborted) setPrevious({ customer: customerPartyId, data });
    }).catch(() => {
      if (!controller.signal.aborted) setPrevious({ customer: customerPartyId, failed: true });
    });
    return () => controller.abort();
  }, [customerPartyId, includeIncome, retryPrevious]);
  useEffect(() => {
    if (!previous?.data || previous.customer !== customerPartyId || attemptedPrevious.current === customerPartyId) return;
    attemptedPrevious.current = customerPartyId;
    const f = getValues('financial');
    const amounts = [f?.income?.grossIncome, f?.income?.netIncome, f?.income?.otherIncome,
      f?.livingExpenses, f?.maintenance, f?.otherLivingExpenses,
      ...Object.values(f?.expenses || {}), ...Object.values(f?.household || {})];
    const hasEntries = hasIndividualBreakdown(f) || amounts.some(value => typeof value === 'number' && value !== 0)
      || f?.otherLivingExpenseItems?.length > 0;
    if (!hasEntries && !getFieldState('financial').isDirty) applyPrevious(previous.data);
  }, [previous, customerPartyId, getValues, getFieldState, applyPrevious]);
  const usingRecorded = !!recorded && !detailed;
  const breakdownLocked = usingRecorded && !captureBreakdown;
  // Returning-customer capture initialises gross to zero even when only take-home pay was saved.
  const recordedGross = recorded?.income?.grossIncome === 0 && recorded?.netIncome > 0
    ? undefined : recorded?.income?.grossIncome;
  const previousTakeHome = net ?? financial?.income?.netIncome;
  const otherTotal = otherLivingExpenseTotal(financial);
  const itemised = financial?.otherLivingExpenseItems != null;
  const expenses = sumMonthlyAmounts([figures.livingExpenses, figures.maintenance, figures.debtPayments]);
  const netIncome = detailed || recorded ? figures.netIncome : undefined;
  const totalIncome = sumMonthlyAmounts([netIncome, figures.otherIncome]);
  const remaining = totalIncome == null || expenses == null ? undefined : Math.round((totalIncome - expenses) * 100) / 100;
  const invalidObligations = (detailed || !!recorded) && (figures.maintenance == null || figures.debtPayments == null);
  useEffect(() => {
    if (editRecorded || (!includeIncome && !detailed)) return;
    if ((!detailed && recorded) || (detailed && figures.grossIncome != null && figures.netIncome != null && totals.necessaryExpenses != null))
      setValue('financial.affordabilityCaptured', true);
    if (detailed && figures.grossIncome != null && figures.netIncome != null) {
      setValue('financial.income.grossIncome', figures.grossIncome);
      setValue('financial.income.netIncome', figures.netIncome);
      setValue('financial.income.otherIncome', 0);
      setValue('individual.monthlyNetIncome', figures.netIncome);
    }
    if (!detailed && recorded && net != null) setValue('financial.income.netIncome', net);
    if (figures.livingExpenses != null) {
      setValue('financial.livingExpenses', figures.livingExpenses);
      setValue('individual.monthlyLivingExpenses', figures.livingExpenses);
    }
    if (itemised) setValue('financial.otherLivingExpenses', otherTotal);
    if (figures.debtPayments != null) setValue('financial.hasExistingCreditObligations', figures.debtPayments > 0);
  }, [editRecorded, includeIncome, detailed, recorded, net, figures.grossIncome, figures.netIncome, totals.necessaryExpenses, figures.livingExpenses, figures.debtPayments, otherTotal, itemised, setValue]);
  const field = (name: string, label: string, id: string) => {
    const error = name.split('.').reduce<any>((item, key) => item?.[key], errors);
    return <div className="individual-money-row" key={name}>
      <label htmlFor={id}>{label}</label>
      <Controller name={name} control={control} rules={{ min: { value: 0, message: 'Enter a non-negative amount.' } }}
        render={({ field: input }) => <MoneyInput id={id} name={input.name} ref={input.ref} value={input.value} blankAsZero
          onValueChange={input.onChange} onBlur={input.onBlur} min={0} className={`form-control ${error ? 'is-invalid' : ''}`}
          aria-invalid={!!error} aria-describedby="individual-money-help" />} />
      {error && <span role="alert" className="text-danger small">{String(error.message)}</span>}
    </div>;
  };
  const group = (index: number) => {
    const section = individualBreakdownGroups[index];
    const total = !detailed ? undefined : index === 0 ? figures.grossIncome : index === 1 ? totals.deductions : figures.livingExpenses;
    return <fieldset className="mb-4" aria-label={section.title} disabled={breakdownLocked}>
      <legend className="h6 mb-3">{section.title}</legend>
      {section.fields.map(item => field(`financial.individualBreakdown.${item.name}`, item.label, `individual-breakdown-${item.name}`))}
      {index === 2 && <OtherLivingCostFields />}
      <div className="d-flex justify-content-between gap-3 border-top pt-3 fw-semibold">
        <span>Total {section.title}</span><span>{money(total)}</span>
      </div>
    </fieldset>;
  };
  const obligations = <fieldset className="mb-4" aria-label="Maintenance and declared repayments">
    <legend className="h6 mb-3">Credit obligations · Maintenance and declared repayments</legend>
    {legacyIndividualAffordabilityGroups[1].fields.slice(8).map(item =>
      field(item.name, `${item.label} (R)`, `obligation-${item.name}`))}
    {invalidObligations && <p role="alert" className="text-danger">Check maintenance and declared repayments under Credit obligations.</p>}
  </fieldset>;
  const currentPrevious = previous?.customer === customerPartyId ? previous : undefined;
  const previousFigures = customerPartyId && includeIncome && <>
    {!currentPrevious && <p role="status">Loading previous income and expenses…</p>}
    {currentPrevious?.failed && <p role="alert">Previous income and expenses could not be loaded.
      <button type="button" className="btn btn-link" onClick={() => setRetryPrevious(value => value + 1)}>Retry loading previous figures</button></p>}
    {currentPrevious?.data && <p className="alert alert-info" role="note">
      {appliedFrom === currentPrevious.data.applicationId
        ? `Prefilled from application ${currentPrevious.data.applicationNumber}. Review these figures for this application.`
        : <>Income and expenses are available from application {currentPrevious.data.applicationNumber}.
          <button type="button" className="btn btn-link" onClick={() => applyPrevious(currentPrevious.data!)}>Use previous income and expenses</button>
          Loading them replaces the financial entries currently shown.</>}
    </p>}
  </>;
  if (editRecorded || (!includeIncome && !detailed)) return <>
    {previousFigures}
    {includeIncome && <button type="button" className="btn btn-link mb-3" onClick={() => {
      setRecorded(readRecorded());
      setEditRecorded(false);
    }}>Back to income and expense breakdown</button>}
    <LegacyIndividualAffordabilityFields includeIncome={includeIncome} />
  </>;
  return <section className="form-section individual-financial-breakdown" aria-labelledby={showHeading ? 'individual-affordability-heading' : undefined}
    aria-label={showHeading ? undefined : 'Income and Expenses'}>
    {showHeading && <h6 id="individual-affordability-heading" className="form-section-title">Income and Expenses</h6>}
    {previousFigures}
    <p id="individual-money-help" className="small text-muted">Amounts in rand per month. Leave unused categories blank; they count as R0.
      Include each cost once; keep maintenance and loan repayments under Credit obligations.</p>
    {includeIncome && !recorded && previousTakeHome > 0 && (!detailed || figures.grossIncome == null) &&
      <p className="alert alert-info" role="note">Previously recorded take-home pay: {money(previousTakeHome)}.
        Enter current income before deductions under Gross Income, then enter the payslip deductions. The form calculates both totals.</p>}
    {recorded && <div className="alert alert-info mb-4" role="note" aria-labelledby="recorded-financial-heading">
      <div className="d-flex align-items-center gap-2 mb-2">
        <h6 id="recorded-financial-heading" className="mb-0">Previously recorded information</h6>
        <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={<Popover id={recordedHelpId}>
          <Popover.Header as="h3">About previously recorded information</Popover.Header>
          <Popover.Body>
            <p>This customer has saved totals without a category breakdown.
              {usingRecorded ? ' These saved figures are currently in use; the blank categories below have not been captured.'
                : ' The current breakdown replaces these saved totals; they are not added together.'}</p>
            {usingRecorded && <p className="mb-0">To replace these totals, capture all current income, payslip deductions and household costs.
              Entering the first category replaces the saved totals; gross salary cannot be inferred from take-home income.</p>}
          </Popover.Body>
        </Popover>}><button type="button" className="btn btn-link p-0" aria-label="About previously recorded information">ⓘ</button></OverlayTrigger>
      </div>
      <p className="small mb-2">{usingRecorded ? 'Saved totals currently in use.' : 'Replaced by the current breakdown.'}</p>
      <dl className="row mb-2"><dt className="col-8">Recorded gross income</dt><dd className="col-4">{money(recordedGross)}</dd>
        <dt className="col-8">Recorded take-home income</dt><dd className="col-4">{money(recorded.netIncome)}</dd>
        <dt className="col-8">Recorded other net income</dt><dd className="col-4">{money(recorded.income?.otherIncome)}</dd></dl>
      {usingRecorded && <>
        <div className="d-flex gap-2 flex-wrap">
          {breakdownLocked && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setCaptureBreakdown(true)}>Capture current breakdown</button>}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditRecorded(true)}>Edit recorded information</button>
        </div>
      </>}
      <details className="mt-2"><summary>Recorded household costs</summary><dl className="row mt-2">
        {legacyIndividualAffordabilityGroups[1].fields.slice(0, 8).map(item => {
          const value = item.name.split('.').slice(1).reduce<any>((previous, key) => previous?.[key], recorded);
          return <React.Fragment key={item.name}><dt className="col-8">{item.label}</dt><dd className="col-4">{money(value)}</dd></React.Fragment>;
        })}</dl></details>
    </div>}
    <div className="row g-4">
      {includeIncome && <section className="col-12 col-lg-6" aria-label="Income and credit obligations">
        {group(0)}{group(1)}
        <div className="d-flex justify-content-between gap-3 border-top pt-3 mb-4 fw-semibold">
          <span>{usingRecorded ? 'Recorded take-home income in use' : 'Total Net Income'}</span><span>{money(netIncome)}</span>
        </div>
        {obligations}
      </section>}
      <div className={includeIncome ? 'col-12 col-lg-6' : 'col-12'}>{group(2)}</div>
    </div>
    {detailed && (totals.grossIncome == null || totals.netIncome == null || totals.necessaryExpenses == null) &&
      <p role="alert" className="text-danger">Enter non-negative amounts to cents. Deductions cannot exceed gross income.</p>}
    {!includeIncome && obligations}
    <div className="row g-3 border-top pt-3" aria-live="polite">
      <div className="col-md-4"><div className="small text-muted">{usingRecorded ? 'Recorded take-home income in use' : 'Net Income'}</div><strong>{money(netIncome)}</strong></div>
      <div className="col-md-4"><div className="small text-muted">Total monthly expenses</div><strong>{money(expenses)}</strong></div>
      <div className="col-md-4"><div className="small text-muted">Income left before the new loan</div><strong className={remaining != null && remaining < 0 ? 'text-danger' : ''}>{money(remaining)}</strong></div>
    </div>
  </section>;
}
